import { useMemo } from 'react';

import type { Option } from '@/components/inputs/AutocompleteInput';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';

import { useGetCommercesQuery } from '../../../services';

interface UseCommercesOptionsProps {
  search?: string;
  minLength?: number;
}

/**
 * Busca comercios por nombre para el autocomplete del wizard. El backend filtra
 * con iLike, así que alcanza con un fragmento del nombre.
 */
const useCommercesOptions = ({
  search,
  minLength = 3,
}: UseCommercesOptionsProps = {}) => {
  const { debouncedSearch, shouldSearch, isDebouncePending } =
    useAutocompleteSearch(search, minLength);

  const { data, isLoading, isFetching } = useGetCommercesQuery(
    {
      paginated: { page: 1, pageSize: 50 },
      filters: { name: shouldSearch ? debouncedSearch : '' },
    },
    { skip: !shouldSearch }
  );

  const resolvedOptions: Option[] = useMemo(
    () =>
      (data?.items ?? []).map((commerce) => ({
        label: commerce.name,
        data: commerce,
      })),
    [data?.items]
  );

  const commerceOptions = useStableOptions(resolvedOptions, {
    isPending: isFetching || isDebouncePending,
    enabled: shouldSearch || isDebouncePending,
  });

  return {
    commerceOptions,
    isLoadingCommerces:
      Boolean(shouldSearch) && (isLoading || isFetching),
  };
};

export default useCommercesOptions;
